/**
 * Всё изменчивое состояние вкладки «Заметки» — НАД самой вкладкой.
 *
 * ПОЧЕМУ ОНО ЗДЕСЬ, А НЕ В ПАНЕЛИ. Неактивная вкладка размонтируется целиком, а не прячется
 * стилем: спрятанный список из двадцати карточек держал бы шестьдесят blob-URL и столько же
 * декодированных картинок в памяти всё время, пока человек читает анализ. Но размонтировать
 * панель вместе с набранным черновиком — значит терять текст за переключение вкладки, то есть
 * ровно то, чего вкладки обещали не делать. Поэтому состояние поднято сюда, к `MapScreen`,
 * который переживает и переключение, и перерисовку консоли.
 *
 * СБРАСЫВАЕТСЯ ЦЕЛИКОМ РОВНО НА ОДНО СОБЫТИЕ — смену выбранного поля. Черновик про это поле,
 * открытый редактор про эту заметку, раскрытые карточки про этот список; ни одно из четырёх
 * не значит ничего на соседнем контуре. Переключение вкладок не трогает ни одного поля.
 */

import { useCallback, useEffect, useState } from "react";

/**
 * Черновик новой заметки: слова, файлы и дата, которых ещё нет нигде, кроме этой вкладки.
 *
 * Дата здесь с первой секунды, а не появляется при сохранении: заметку из консоли заводят про
 * день, когда были на поле, и «сегодня» — умолчание, которое человек вправе поправить ДО
 * отправки, а не после.
 */
export interface NoteDraft {
  text: string;
  files: File[];
  /** ISO date, как в `<input type="date">`. */
  date: string;
  /**
   * Место, выбранное ДО слов: тык по карте в режиме тулбара ставит маркер и открывает
   * создание уже с координатой. Заметка, заведённая кнопкой панели, рождается без места —
   * его укажут потом или не укажут никогда, и это законно.
   */
  place?: { lat: number; lon: number };
}

/**
 * Открытый редактор заметки — состояние правой колонки модалки.
 *
 * Дата здесь ЗАКОННЫЙ ТРЕТИЙ ЧЛЕН, хотя первая спека называла два: редактор правит `note` и
 * `happened_on` ОДНИМ PATCH — правка даты и есть правка заметки, отдельной ручки у неё нет
 * (арбитраж лида, дельта happened_on §5). Держать дату вне состояния значило бы читать её из
 * пропса посреди набора и терять при каждом приходе новых данных с сервера.
 */
export interface NoteEditing {
  noteId: number;
  text: string;
  /** ISO date, как в `<input type="date">`. */
  date: string;
}

/**
 * Что показывает модалка. `null` — закрыта.
 *
 * Живёт ЗДЕСЬ, а не в самой модалке, по той же причине, что и черновик: панель «Заметки»
 * размонтируется вместе с вкладкой, а окно обязано пережить и переключение вкладки, и
 * перерисовку консоли. `photoId` — с какого кадра открыта галерея (`null` — с первого);
 * `editing` — открыта ли сразу правка (карандаш на карточке ведёт прямо в неё).
 */
export interface NoteOpen {
  noteId: number;
  photoId: number | null;
  editing: boolean;
}

export interface NotesUi {
  draft: NoteDraft | null;
  editing: NoteEditing | null;
  /**
   * Какая заметка открыта в модалке. Заменил собой `expanded`: раскрытия карточки больше нет
   * — всё, что о заметке можно узнать не открывая, карточка показывает сразу, а всё
   * остальное показывает окно.
   */
  open: NoteOpen | null;
  /**
   * Чьё место сейчас указывают на карте. Зеркало `markerTarget`, поднятое сюда ради чипа в
   * карточке: сама карточка о режиме карты не знает и знать не должна.
   */
  placingNoteId: number | null;
}

const EMPTY: NotesUi = { draft: null, editing: null, open: null, placingNoteId: null };

export interface NotesUiApi extends NotesUi {
  setDraft: (draft: NoteDraft | null) => void;
  setEditing: (editing: NoteEditing | null) => void;
  setOpen: (open: NoteOpen | null) => void;
  setPlacingNoteId: (noteId: number | null) => void;
  reset: () => void;
}

export const useNotesUi = (selectedFieldId: number | null): NotesUiApi => {
  const [ui, setUi] = useState<NotesUi>(EMPTY);

  // Смена поля — единственное, что сносит всё. Переключение вкладок сюда не доходит вовсе:
  // хук живёт над консолью и о вкладках не знает.
  useEffect(() => {
    setUi(EMPTY);
  }, [selectedFieldId]);

  const setDraft = useCallback((draft: NoteDraft | null) => {
    setUi((current) => ({ ...current, draft }));
  }, []);

  const setEditing = useCallback((editing: NoteEditing | null) => {
    setUi((current) => ({ ...current, editing }));
  }, []);

  const setOpen = useCallback((open: NoteOpen | null) => {
    setUi((current) => ({ ...current, open }));
  }, []);

  const setPlacingNoteId = useCallback((noteId: number | null) => {
    setUi((current) => ({ ...current, placingNoteId: noteId }));
  }, []);

  const reset = useCallback(() => setUi(EMPTY), []);

  return { ...ui, setDraft, setEditing, setOpen, setPlacingNoteId, reset };
};
